import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { AuthStore } from '../store/auth.store';

/**
 * Cada cuánto se manda un latido mientras la pestaña está en uso. Dos minutos
 * cubren de sobra la ventana de 5 minutos con la que el panel decide quién está
 * «ahora mismo», y a la vez reducen a la mitad las peticiones autenticadas que
 * el gateway resuelve contra el core.
 */
const HEARTBEAT_INTERVAL = 120_000;
/** Sin interacción durante este tiempo se deja de latir, aunque la pestaña siga visible. */
const IDLE_LIMIT = 15 * 60_000;
/** Margen mínimo entre latidos, para que volver a la pestaña no dispare uno por evento. */
const MIN_GAP = 60_000;

const ACTIVITY_EVENTS = ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'];

/**
 * Latido de presencia hacia el gateway. Sirve al Dashboard de actividad del panel
 * admin para saber cuánta gente está usando la aplicación de verdad.
 *
 * Late SÓLO si la pestaña está visible y ha habido interacción reciente: la app
 * refresca cosas en segundo plano (configuración, saldos, notificaciones), así que
 * contar peticiones daría por «activo» a quien tiene una pestaña olvidada abierta.
 * Es fire-and-forget: cualquier fallo se ignora, nunca molesta al usuario.
 */
@Injectable({ providedIn: 'root' })
export class ActivityHeartbeatService {
  private api = inject(ApiService);
  private authStore = inject(AuthStore);

  private intervalId: any = null;
  private lastInteractionAt = Date.now();
  private lastBeatAt = 0;
  private started = false;

  private readonly onInteraction = () => {
    this.lastInteractionAt = Date.now();
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.lastInteractionAt = Date.now();
      void this.beat();
    }
  };

  start(): void {
    if (this.started) return;
    this.started = true;

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, this.onInteraction, { passive: true })
    );
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.lastInteractionAt = Date.now();
    void this.beat();
    this.intervalId = setInterval(() => void this.beat(), HEARTBEAT_INTERVAL);
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;

    ACTIVITY_EVENTS.forEach((event) => window.removeEventListener(event, this.onInteraction));
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  private async beat(): Promise<void> {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
    if (!this.authStore.isAuthenticated() || this.authStore.closingSession()) return;

    const now = Date.now();
    if (now - this.lastInteractionAt > IDLE_LIMIT) return;
    if (now - this.lastBeatAt < MIN_GAP) return;

    this.lastBeatAt = now;
    try {
      // Un latido que falla (429, 401 al caducar la sesión, red) no debe sacar avisos
      // por pantalla (silentError) ni cerrar la sesión o navegar a otra ruta
      // (background): de reaccionar al 401 ya se encarga la siguiente petición real
      // del usuario. Sin `background`, CoreService emitiría EVENTS.logout_user aun con
      // silentError, y el latido echaría al usuario al login sin que hiciera nada.
      await this.api.post('v1/activity/heartbeat', {
        body: { app: 'superpay-web' },
        silentError: true,
        background: true
      });
    } catch {
      // Sin reintento: si se pierde una muestra, la siguiente llega en un minuto.
    }
  }
}
